import { VisaDocumentModel } from 'api-hooks/visa-document';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { FormAutoSaver } from 'common/helpers/form-auto-saver';
import { useFormState } from 'components/elements/form';
import Popover, { PopoverMenuType } from 'components/popover';
import { useAuthorization } from 'hooks/use-authorization';
import useDestroyAction from 'hooks/use-destroy-action';
import useNavigation from 'hooks/use-navigation';
import { visaDocumentToDocumentPurchase } from 'legacy-modules/order/travel-document/hooks/use-get-default-values';
import { visaDocumentToDocumentSales } from 'legacy-modules/sales/travel-document/hooks/use-get-default-values';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

interface VisaDocumentActionComponentProps {
  visaDocument: VisaDocumentModel;
}

export default function VisaDocumentActionComponent(
  props: VisaDocumentActionComponentProps,
) {
  const { navigate } = useNavigation();
  const { t } = useTranslation();
  const { visaDocument } = props;
  const { can } = useAuthorization();

  const { deleteAction } = useDestroyAction(
    'visa-documents',
    props.visaDocument.id,
  );
  const { editable, setIsEditable } = useFormState();

  const editPermission = can(AuthorizationRules.VisaDocumentsUpdate);
  const editAction: PopoverMenuType = {
    key: `edit-visa-document-${props.visaDocument.id}`,
    label: t('common:edit'),
    onClick() {
      setIsEditable(true);
    },
    isAllowed: editPermission,
  };

  const createInvoicePermission = can(AuthorizationRules.DocumentSalesCreate);
  const createInvoiceAction: PopoverMenuType = {
    key: `create-invoice-${props.visaDocument.id}`,
    label: t('common:create_extra', { extra: t('common:invoice') }),
    onClick: () => {
      const defaultValues = visaDocumentToDocumentSales(visaDocument);
      FormAutoSaver.remove(NavigationRoutes.TravelDocumentSalesCreate);
      navigate(NavigationRoutes.TravelDocumentSalesCreate, {
        defaultValues,
      });
    },
    isAllowed: createInvoicePermission,
  };

  const createOrderPermission = can(AuthorizationRules.DocumentPurchasesCreate);
  const createOrderAction: PopoverMenuType = {
    key: `create-order-${props.visaDocument.id}`,
    label: t('common:create_extra', { extra: t('common:order') }),
    onClick: () => {
      const defaultValues = visaDocumentToDocumentPurchase(visaDocument);
      FormAutoSaver.remove(NavigationRoutes.DocumentPurchasesCreate);
      navigate(NavigationRoutes.DocumentPurchasesCreate, {
        defaultValues,
      });
    },
    isAllowed: createOrderPermission,
  };

  const actions = [
    editAction,
    createInvoiceAction,
    createOrderAction,
    deleteAction,
  ].filter((action) => action.isAllowed);

  if (editable) return null;
  if (actions.length === 0) return null;

  return (
    <>
      <Popover actions={actions} />
    </>
  );
}
